'use client'

import { type ComponentType, useCallback, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import type { ParseKeys } from 'i18next'
import { animate, motion, useInView, useMotionValue, useTransform } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { setI18nNamespace, tKey } from '@/utils/i18n'
import { Heading } from 'ui/src/components/Heading/Heading'
import { Text } from 'ui/src/components/Text/Text'
import { Flow01Home } from './flows/Flow01Home/Flow01Home'
import { Flow02Chat } from './flows/Flow02Chat/Flow02Chat'
import { Flow03Insurance } from './flows/Flow03Insurance/Flow03Insurance'
import { Flow04Claims } from './flows/Flow04Claims/Flow04Claims'
import { Flow05Forever } from './flows/Flow05Forever/Flow05Forever'
import { type FlowProps } from './flows/flows.types'
import { PhoneFrame } from './PhoneFrame/PhoneFrame'
import {
  desktopStepper,
  dotButton,
  dotFill,
  dotsRow,
  dotTrack,
  heading,
  mobileLabel,
  mobileNumber,
  mobileStepper,
  phoneFrameContainer,
  progressFill,
  progressTrack,
  progressTrackActive,
  progressTrackInactive,
  section,
  stepButton,
  stepper,
  stepText,
  stepTextInactive,
  swipeItem,
  swipeTrack,
  swipeViewport,
} from './AppShowcase.css'

setI18nNamespace('appShowcase')

type AppShowcaseKey = ParseKeys<'appShowcase'>

type Flow = {
  id: number
  labelKey: AppShowcaseKey
  component: ComponentType<FlowProps>
  duration: number
}

const DEFAULT_FLOW_DURATION_MS = 8000

const flows: Array<Flow> = [
  {
    id: 1,
    labelKey: tKey<'appShowcase'>('FLOW_01_STEP_LABEL'),
    component: Flow01Home,
    duration: 4500,
  },
  {
    id: 2,
    labelKey: tKey<'appShowcase'>('FLOW_02_STEP_LABEL'),
    component: Flow02Chat,
    duration: DEFAULT_FLOW_DURATION_MS,
  },
  {
    id: 3,
    labelKey: tKey<'appShowcase'>('FLOW_03_STEP_LABEL'),
    component: Flow03Insurance,
    duration: 5000,
  },
  {
    id: 4,
    labelKey: tKey<'appShowcase'>('FLOW_04_STEP_LABEL'),
    component: Flow04Claims,
    duration: DEFAULT_FLOW_DURATION_MS,
  },
  {
    id: 5,
    labelKey: tKey<'appShowcase'>('FLOW_05_STEP_LABEL'),
    component: Flow05Forever,
    duration: 5500,
  },
]

// For development purposes, we can freeze the app showcase
const DEV_FREEZE = false

export function AppShowcase() {
  const { t } = useTranslation('appShowcase')
  const sectionRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const inView = useInView(sectionRef, { amount: 0.25 })
  const progress = useMotionValue(0)
  const progressWidth = useTransform(progress, (value) => `${value}%`)

  const advance = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % flows.length)
  }, [])

  const stepBack = () => {
    setActiveIndex((prev) => prev - 1)
  }

  // The progress animation is the auto-advance timer: it lives on a motion value
  // (not the visible fills) so it keeps running whichever stepper CSS hides.
  useEffect(() => {
    if (DEV_FREEZE) return
    // If the section is not in view, do not start the timer
    if (!inView) return

    progress.set(0)
    const controls = animate(progress, 100, {
      duration: flows[activeIndex].duration / 1000,
      ease: 'linear',
      onComplete: advance,
    })
    // Cleanup the animation when the component unmounts
    return () => controls.stop()
  }, [activeIndex, advance, inView, progress])

  const handleStepClick = (index: number) => {
    setActiveIndex(index)
  }

  // Mobile swipe navigation: record touch start on swipeViewport, then on release
  // advance to the next/previous step if the horizontal distance exceeds 40px.
  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    event.currentTarget.dataset.touchX = String(event.touches[0].clientX)
  }

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    const startX = Number(event.currentTarget.dataset.touchX ?? 0)
    const endX = event.changedTouches[0].clientX
    const diff = startX - endX
    if (diff > 40 && activeIndex < flows.length - 1) {
      handleStepClick(activeIndex + 1)
    } else if (diff < -40 && activeIndex > 0) {
      handleStepClick(activeIndex - 1)
    }
  }

  const ActiveFlow = flows[activeIndex].component
  // Each flow drives its own internal timers, so resetting the progress bar is
  // not enough to replay it. Keying on inView remounts the flow when the section
  // re-enters the viewport, starting its choreography over from the first step.
  const flowKey = `${activeIndex}-${inView}`

  return (
    <section className={section} ref={sectionRef}>
      <Heading as="h2" variant="standard.32" className={heading}>
        {t('HEADING')}
      </Heading>

      <div className={phoneFrameContainer}>
        <button
          className={stepper}
          data-side="left"
          aria-label={t('STEPPER_PREVIOUS_BUTTON_LABEL')}
          onClick={stepBack}
          disabled={activeIndex === 0}
        />
        <PhoneFrame>
          <ActiveFlow key={flowKey} onComplete={advance} />
        </PhoneFrame>
        <button
          className={stepper}
          data-side="right"
          aria-label={t('STEPPER_NEXT_BUTTON_LABEL')}
          onClick={advance}
          disabled={activeIndex === flows.length - 1}
        />
      </div>

      <div className={desktopStepper}>
        {flows.map((flow, index) => (
          <button key={flow.id} type="button" onClick={() => handleStepClick(index)} className={stepButton}>
            <Text
              as="span"
              size="md"
              color={index === activeIndex ? 'textPrimary' : 'textSecondary'}
              className={index === activeIndex ? stepText : stepTextInactive}
            >
              {`0${flow.id}`}
            </Text>
            <Text
              size="md"
              color={index === activeIndex ? 'textPrimary' : 'textSecondary'}
              className={index === activeIndex ? stepText : stepTextInactive}
            >
              {t(flow.labelKey)}
            </Text>
            <div className={clsx(progressTrack, index === activeIndex ? progressTrackActive : progressTrackInactive)}>
              {index === activeIndex && <motion.div className={progressFill} style={{ width: progressWidth }} />}
            </div>
          </button>
        ))}
      </div>

      <div className={mobileStepper}>
        <div className={dotsRow}>
          {flows.map((flow, index) => (
            <button key={flow.id} type="button" onClick={() => handleStepClick(index)} className={dotButton}>
              <div className={dotTrack} />
              {index === activeIndex && <motion.div className={dotFill} style={{ width: progressWidth }} />}
            </button>
          ))}
        </div>

        <div className={swipeViewport} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
          <motion.div
            className={swipeTrack}
            animate={{ x: `${-activeIndex * 240}px` }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {flows.map((flow, index) => (
              <div key={flow.id} className={swipeItem}>
                <Text
                  size="md"
                  align="center"
                  color={index === activeIndex ? 'textPrimary' : 'textTertiary'}
                  className={mobileNumber}
                >
                  {`0${flow.id}`}
                </Text>
                <Text
                  size="md"
                  align="center"
                  balance
                  color={index === activeIndex ? 'textPrimary' : 'textTertiary'}
                  className={mobileLabel}
                >
                  {t(flow.labelKey)}
                </Text>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  )
}
